import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HelpCircle, X, ChevronRight, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const HELP_CONTENT = {
  "/dashboard": {
    title: "Dashboard",
    tips: [
      { text: "Complete your Daily Check-in to unlock today's AI training plan", action: "/questionnaire", label: "Go to Check-in" },
      { text: "Your LumiFit Score reflects physical, cognitive, and consistency metrics", action: "/score", label: "View Score" },
      { text: "Set personal goals and track streaks for daily motivation" },
      { text: "Check Mindfulness for AI-guided meditations and sleep stories", action: "/mindfulness", label: "Mindfulness" },
    ],
  },
  "/questionnaire": {
    title: "Daily Check-in",
    tips: [
      { text: "Answer honestly — your responses shape today's AI training plan" },
      { text: "The questionnaire takes under 2 minutes" },
      { text: "Mood, stress, energy, and sleep data are encrypted for your privacy" },
      { text: "Your plan updates each time you check in" },
    ],
  },
  "/training": {
    title: "Training Plan",
    tips: [
      { text: "Your plan is AI-generated based on today's check-in results" },
      { text: "Tap an exercise to see instructions, then mark it complete" },
      { text: "Completing exercises earns XP and builds your streak" },
      { text: "Need more control? Try the Workout Lab", action: "/workout-lab", label: "Workout Lab" },
    ],
  },
  "/mindfulness": {
    title: "Mindfulness Hub",
    tips: [
      { text: "AI Guided — personalized meditation with voice narration (Premium: 3/day)" },
      { text: "Sleep Stories — immersive bedtime narratives to drift off (Premium: 2/day)" },
      { text: "Breathing exercises are free and great for quick stress relief" },
      { text: "Downloaded meditations work offline" },
    ],
  },
  "/games": {
    title: "Brain Games",
    tips: [
      { text: "Cognitive games train memory, focus, and reaction time" },
      { text: "Play daily to improve your cognitive score" },
      { text: "Premium unlocks all game types" },
    ],
  },
  "/progress": {
    title: "Progress Tracker",
    tips: [
      { text: "View your weekly and monthly trends" },
      { text: "Physical and cognitive scores are tracked separately" },
      { text: "The heatmap shows your activity consistency" },
    ],
  },
  "/achievements": {
    title: "Achievements",
    tips: [
      { text: "Earn badges by completing challenges and maintaining streaks" },
      { text: "Share your achievements with friends via the share button" },
      { text: "New achievements unlock as you progress" },
    ],
  },
  "/workout-lab": {
    title: "Workout Lab",
    tips: [
      { text: "Build custom workout routines from our exercise library" },
      { text: "Filter by muscle group, equipment, or difficulty" },
      { text: "Save routines for quick access on training days" },
    ],
  },
  "/deep-dive": {
    title: "Deep Dive",
    tips: [
      { text: "Clinical-grade psychological questionnaires for self-insight" },
      { text: "Results are private, encrypted, and never shared" },
      { text: "Take assessments periodically to track your mental health journey" },
    ],
  },
  "/settings": {
    title: "Settings",
    tips: [
      { text: "Update your profile, avatar, and health information" },
      { text: "Manage your language preference (32 languages supported)" },
      { text: "GDPR tools: export or delete your data at any time" },
    ],
  },
  "/subscription": {
    title: "Premium",
    tips: [
      { text: "Premium unlocks unlimited AI plans, all brain games, and meditation" },
      { text: "Choose Monthly ($17/mo) or Yearly ($150/yr — save over 26%)" },
      { text: "Cancel anytime from your subscription settings" },
    ],
  },
};

const DEFAULT_TIPS = {
  title: "LumiFit Help",
  tips: [
    { text: "Start with the Daily Check-in to get your personalized plan", action: "/questionnaire", label: "Check-in" },
    { text: "Explore Brain Games for cognitive training", action: "/games", label: "Games" },
    { text: "Try AI Meditation for guided relaxation", action: "/mindfulness", label: "Mindfulness" },
    { text: "Track your progress across all areas", action: "/progress", label: "Progress" },
  ],
};

const HelpAgent = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const panelRef = useRef(null);

  const currentPath = location.pathname;
  const helpData = HELP_CONTENT[currentPath] || DEFAULT_TIPS;

  useEffect(() => {
    setOpen(false);
  }, [currentPath]);

  // Keyboard shortcut: press "?" to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
        setOpen(prev => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Don't show on landing/auth/public pages
  const hiddenPages = ["/", "/auth", "/privacy", "/blog", "/contact"];
  if (hiddenPages.includes(currentPath)) return null;

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-[60] w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          open
            ? "bg-slate-700 text-white rotate-90 scale-95"
            : "bg-gradient-to-br from-sky-400 to-indigo-500 text-white hover:shadow-xl hover:scale-105"
        }`}
        data-testid="help-agent-btn"
        aria-label="Help"
      >
        {open ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Help Panel */}
      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-20 right-6 z-[60] w-80 max-h-[70vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
          data-testid="help-agent-panel"
        >
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-sky-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
                {helpData.title}
              </h3>
            </div>
          </div>

          {/* Tips List */}
          <div className="p-4 space-y-3 overflow-y-auto max-h-[50vh]">
            {helpData.tips.map((tip, i) => (
              <div key={`tip-${tip.text.slice(0, 20)}-${i}`} className="flex gap-3 items-start group">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-sky-600 dark:text-sky-400">{i + 1}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{tip.text}</p>
                  {tip.action && (
                    <button
                      onClick={() => { navigate(tip.action); setOpen(false); }}
                      className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 transition-colors"
                      data-testid={`help-tip-link-${i}`}
                    >
                      {tip.label} <ArrowRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700">
            <p className="text-[10px] text-slate-400 text-center">
              Press <kbd className="px-1 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500 text-[9px]">?</kbd> to toggle help
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpAgent;
